if (!customElements.get('citrus-pear-standard')) {
  customElements.define(
    'citrus-pear-standard',
    class CitrusPearStandard extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-cp-standard-viewport]');
        this.track = this.querySelector('[data-cp-standard-track]');
        this.slides = Array.from(this.querySelectorAll('[data-cp-standard-slide]'));
        this.previousButton = this.querySelector('[data-cp-standard-previous]');
        this.nextButton = this.querySelector('[data-cp-standard-next]');
        this.dots = Array.from(this.querySelectorAll('[data-cp-standard-dot]'));
        this.index = 0;

        if (!this.viewport || !this.track || this.slides.length === 0) return;

        this.previousButton?.addEventListener('click', () => this.step(-1));
        this.nextButton?.addEventListener('click', () => this.step(1));
        this.dots.forEach((dot, dotIndex) => {
          dot.addEventListener('click', () => this.goToPage(dotIndex));
        });

        this.viewport.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') this.step(-1);
          if (event.key === 'ArrowRight') this.step(1);
        });

        let scrollTimer;
        this.viewport.addEventListener('scroll', () => {
          this.updateVisibleDividers();
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => this.syncFromScroll(), 80);
        }, { passive: true });

        this.resizeObserver = new ResizeObserver(() => this.updateLayout());
        this.resizeObserver.observe(this.viewport);
        this.updateLayout();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      get compactLayout() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      get maxIndex() {
        if (!this.viewport || !this.slides.length) return 0;
        const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
        if (maxScroll <= 1) return 0;

        const firstOffset = this.slides[0].offsetLeft;
        return this.slides.reduce((closestIndex, slide, index) => (
          Math.abs((slide.offsetLeft - firstOffset) - maxScroll)
            < Math.abs((this.slides[closestIndex].offsetLeft - firstOffset) - maxScroll)
            ? index
            : closestIndex
        ), 0);
      }

      get visibleCount() {
        if (!this.viewport || !this.slides.length) return 1;

        const firstSlide = this.slides[0];
        const secondSlide = this.slides[1];
        const slideStep = secondSlide
          ? secondSlide.offsetLeft - firstSlide.offsetLeft
          : firstSlide.getBoundingClientRect().width;

        return Math.max(1, Math.round(this.viewport.clientWidth / Math.max(1, slideStep)));
      }

      get pageStarts() {
        const pageStarts = [];
        const pageSize = this.visibleCount;
        const lastStart = this.maxIndex;

        for (let index = 0; index <= lastStart; index += pageSize) {
          pageStarts.push(index);
        }

        if (pageStarts[pageStarts.length - 1] !== lastStart) pageStarts.push(lastStart);
        return pageStarts;
      }

      updateLayout() {
        if (this.wasCompactLayout !== this.compactLayout) {
          this.index = 0;
          this.viewport.scrollTo({ left: 0, behavior: 'auto' });
          this.wasCompactLayout = this.compactLayout;
        }

        const compactSlideWidth = Math.max(1, (this.viewport.clientWidth - 8) / 2);
        this.slides.forEach((slide) => {
          slide.style.flexBasis = this.compactLayout ? `${compactSlideWidth}px` : '';
        });

        const isStatic = this.maxIndex === 0;
        this.classList.toggle('is-static', isStatic);
        this.index = Math.min(this.index, this.maxIndex);
        const pageCount = this.pageStarts.length;
        this.dots.forEach((dot, dotIndex) => {
          dot.hidden = dotIndex >= pageCount;
          dot.setAttribute('aria-label', `Show ingredient page ${dotIndex + 1}`);
        });
        this.updateControls();
      }

      step(direction) {
        const pageStarts = this.pageStarts;
        const nextPageStart = direction > 0
          ? pageStarts.find((pageStart) => pageStart > this.index)
          : pageStarts.slice().reverse().find((pageStart) => pageStart < this.index);

        if (nextPageStart !== undefined) this.goTo(nextPageStart);
      }

      goToPage(pageIndex) {
        const targetIndex = this.pageStarts[pageIndex];
        if (targetIndex !== undefined) this.goTo(targetIndex);
      }

      goTo(index, behavior = 'smooth') {
        const nextIndex = Math.max(0, Math.min(index, this.maxIndex));
        const target = this.slides[nextIndex];
        const first = this.slides[0];
        if (!target || !first) return;

        this.index = nextIndex;
        this.viewport.scrollTo({
          left: target.offsetLeft - first.offsetLeft,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior,
        });
        this.updateControls();
      }

      syncFromScroll() {
        const first = this.slides[0];
        if (!first) return;
        const currentLeft = this.viewport.scrollLeft + first.offsetLeft;
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - currentLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });
        this.index = Math.min(closestIndex, this.maxIndex);
        this.updateControls();
      }

      updateControls() {
        const pageStarts = this.pageStarts;
        const activePage = pageStarts.reduce((closestPage, pageStart, pageIndex) => (
          Math.abs(pageStart - this.index) < Math.abs(pageStarts[closestPage] - this.index)
            ? pageIndex
            : closestPage
        ), 0);

        if (this.previousButton) this.previousButton.disabled = this.index <= pageStarts[0];
        if (this.nextButton) this.nextButton.disabled = this.index >= pageStarts[pageStarts.length - 1];
        this.dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === activePage;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        this.updateVisibleDividers();
      }

      updateVisibleDividers() {
        this.slides.forEach((slide) => slide.classList.remove('has-visible-divider'));
        if (this.compactLayout) return;

        const viewportRect = this.viewport.getBoundingClientRect();
        const visibleSlides = new Set(
          this.slides.filter((slide) => {
            const slideRect = slide.getBoundingClientRect();
            const center = slideRect.left + slideRect.width / 2;
            return center > viewportRect.left + 1 && center < viewportRect.right - 1;
          })
        );

        this.slides.forEach((slide, index) => {
          if (index > 0 && visibleSlides.has(slide) && visibleSlides.has(this.slides[index - 1])) {
            slide.classList.add('has-visible-divider');
          }
        });
      }
    }
  );
}
